import { BaseEntity, ErrorResponse, LocalDate, NumberDate, NumberMonth, Timestamp, TuettuKieli } from '@lemon/core/model/common'
import { PaymentSupportedBank } from '@lemon/core/model/payment'
import { LaskutettavatPalvelut, LaskutusFrekvenssi, TyojononMerkintaBase } from './lasku'
import { PushTilaukset } from './push-ilmoitus'
import { SahkoisenTiliotteenAlatyyppi } from './tiliote'
import {
  AUTOMAATTISET_JAKSOTUKSET_ID,
  MUU_MAKSUTAPA_ID,
  MYYNTILASKU_MAKSUTAPA_ID,
  MYYNTITOSITE_MAKSUTAPA_ID,
  PALKKATOSITE_MAKSUTAPA_ID,
  SAHKOISET_LASKUT_FAKE_MAKSUTAPA_ID,
  SAHKOISET_LASKUT_MAKSUTAPA_ID,
  TILIOTETOSITE_MAKSUTAPA_ID,
} from './tosite'

export const LEMONTREE_ASIAKAS_AVAIN = 'jzATU474P4BRCkEANZ1H'

export const ytjAlvrekisterinTilat = [
  'Liiketoiminnasta arvonlisäverovelvollinen',
  'Kiinteistön käyttöoikeuden luovuttamisesta',
  'Taide-esineen myynnistä tai tekijänä arvonlisäverovelvollinen',
  'Aiheeton rivi',
  'Alkutuottajana arvonlisäverovelvollinen',
  'Alkutuottajana ja/tai kuvataiteilijana arvonlisäverovelvollinen',
  'Paliskunnan osakkaana',
  'EU-palvelumyynneistä ilmoituksenantovelvollinen',
  'Ostajana tai omasta käytöstä arvonlisäverovelvollinen',
]
export type YtjAlvrekisterinTila = (typeof ytjAlvrekisterinTilat)[number]

export enum Yritysmuoto {
  TOIMINIMI = 't',
  AVOINYHTIO = 'a',
  KOMMANDIITTIYHTIO = 'k',
  OSAKEYHTIO = 'o',
  OSUUSKUNTA = 'os',
  YHDISTYS = 'y',
}

/**
 * Represents a feature in the system
 */
export enum UserFeature {
  /** User can edit invoice penalty interest */
  CAN_EDIT_PENALTY_INTEREST = 'CAN_EDIT_PENALTY_INTEREST',
}

export enum Roolit {
  // Laskutus
  LASKUTUS = 'LASKUTUS',

  // Maksut
  MAKSUT = 'MAKSUT',
  MAKSUT_HYVAKSYJA = 'MAKSUT_HYVAKSYJA',
  MAKSUT_MAKSAJA = 'MAKSUT_MAKSAJA',
  MAKSUT_VASTAANOTTAJA = 'MAKSUT_VASTAANOTTAJA',

  // Tositteet
  TOSITTEET_OSTO = 'TOSITTEET_OSTO',
  TOSITTEET_MYYNTI = 'TOSITTEET_MYYNTI',
  TOSITTEET_TILI = 'TOSITTEET_TILI',
  TOSITTEET_PALKKA = 'TOSITTEET_PALKKA',

  // Laskujen vastaanotto
  SAHKOISTEN_LASKUJEN_VASTAANOTTO = 'SAHKOISTEN_LASKUJEN_VASTAANOTTO',

  // Palkat
  PALKAT = 'PALKAT',

  // Raportit
  RAPORTIT = 'RAPORTIT',

  // Hallinto
  HALLINTO_SOPIMUKSEN_HYVAKSYJA = 'HALLINTO_SOPIMUKSEN_HYVAKSYJA',
  HALLINTO_YHTEYSHENKILO = 'HALLINTO_YHTEYSHENKILO',
  HALLINTO_SUOMI_FI_VALTUUTUKSEN_ANTAJA = 'HALLINTO_SUOMI_FI_VALTUUTUKSEN_ANTAJA',
  HALLINTO_TUNTEMISTIETOJEN_ANTAJA = 'HALLINTO_TUNTEMISTIETOJEN_ANTAJA',
  HALLINTO_VIIA_VALTUUTUKSEN_ANTAJA = 'HALLINTO_VIIA_VALTUUTUKSEN_ANTAJA',

  TUNNISTETAAN = 'TUNNISTETAAN',
}

export type SelvitettavienNakyvyys = 'kaikki' | 'omat'
export interface Kayttaja {
  avain: string
  etunimi: string
  sukunimi: string
  email: string
  puhelinnumero?: string
  aktiivinen: boolean
  prosessinOsat: TositteenLisaysprosessinOsaMobiilissa[]
  alvVahennysoikeusPortaat: TositteenLisaysprosessinAlvVahennysoikeusPorras[]
  tositteenMaksutavat: TositteenMaksutapa[]
  roolit: { [rooli in Roolit]?: boolean }
  kirjauduKayttajanaUid: string | null
  asiakasId: number
  asiakasAvain: string
  kayttajaTunnistettu: KayttajanTunnistamisStatus
  sopimuksenHyvaksyntaStatus: SopimuksenHyvaksyntaStatus
  asiointikieli: TuettuKieli
  tervetuloaDialogiNaytetty: boolean
  selvitettavienNakyvyys: SelvitettavienNakyvyys
  pepTiedotAnnettu?: boolean
  features?: { [rooli in UserFeature]?: boolean }
  moniyritys?: true
}

export interface KayttajanMoniyritysYritys {
  kayttajaAvain: string
  asiakasAvain: string
  asiakasId: number
}

/**
 * Backend-only-dokumentti polussa kayttajan-moniyritystiedot/{kayttajaAvain}.
 * Kirjoitetaan ainoastaan Lemonatorin synkasta, luetaan ainoastaan
 * Cloud Functioneista — Firestore-säännöissä ei tarkoituksella ole matchia.
 */
export interface KayttajanMoniyritystiedot {
  kayttajaAvain: string
  tukiAvain: string
  yritykset: KayttajanMoniyritysYritys[]
  paivitetty: Timestamp
}

/**
 * Lemonaid-mobiilin Firebase-installaatiot polussa
 * customers/{asiakasAvain}/customers-users/{kayttajaAvain}/user-firebase-installations/{kayttajaAvain}
 * (yksi dokumentti per käyttäjä). Kirjoitetaan ainoastaan
 * mobiili-fidTallenna-callablesta ja push-ilmoitus-työjonon
 * käsittelijästä, luetaan ainoastaan Cloud Functioneista — Firestore-säännöissä
 * ei tarkoituksella ole matchia. Tokeniton kopio webille ja Lemonatorille on
 * KayttajanLaitteet, joka kirjoitetaan samassa batchissa.
 */
export interface KayttajanFirebaseInstallations {
  /**
   * Laitteen tuoreimmat tunnisteet FID:llä avainnettuna. FID (Firebase
   * Installation ID) on asennuksen pysyvä avain, jonka alla token korvautuu
   * rotatoituessa — yksi entry per laite. Kuolleiksi todetut laitteet
   * poistetaan mapista push-lähetyksen yhteydessä.
   */
  fids: { [fid: string]: KayttajanFid }
  asiakasAvain: string
  kayttajaAvain: string
}

/**
 * Vastaa firebase_messagingin AuthorizationStatus-enumin nimiä. Tallennetaan
 * merkkijonona: tuntematon arvo ei saa estää FID:n tallennusta.
 */
export type PushLupa = 'authorized' | 'denied' | 'notDetermined' | 'provisional' | 'deniedPermanently' | (string & {})

/** Vastaa firebase_messagingin AppleNotificationSetting-enumin nimiä. */
export type PushIosAsetus = 'enabled' | 'disabled' | 'notSupported' | (string & {})

/**
 * Vain iOS. Lupa 'authorized' ei kerro näkyykö ilmoitus: provisional-luvasta
 * peritty hiljainen jakelu jättää vain notificationCenterin päälle, jolloin
 * ilmoitus toimitetaan mutta ei näy bannerina, lukitusnäytöllä eikä äänenä.
 */
export interface PushIosAsetukset {
  alert?: PushIosAsetus
  lockScreen?: PushIosAsetus
  notificationCenter?: PushIosAsetus
  sound?: PushIosAsetus
  badge?: PushIosAsetus
}

export interface KayttajanFid {
  fid: string
  /** FCM-rekisteröintitoken — push-ilmoituksen lähetysosoite. Puuttuu jos laite ei ole saanut tokenia. */
  token?: string
  lahde: 'android' | 'ios'
  paivitetty: Timestamp
  /** Ensimmäinen rekisteröinti tällä FID:llä. Puuttuu ennen 9/2026 rekisteröityneiltä laitteilta. */
  luotu?: Timestamp
  lupa?: PushLupa
  /** Vain iOS: saiko laite APNS-tokenin. Ilman sitä FCM-tokenia ei synny. */
  apnsTokenSaatavilla?: boolean
  iosAsetukset?: PushIosAsetukset
  tilaukset?: PushTilaukset
  laite?: KayttajanLaitetiedot
  sovellus?: KayttajanSovellustiedot
}

/** Laitteen tunnistetiedot rekisteröintihetkellä. Ei käyttäjän antamaa laitenimeä. */
export interface KayttajanLaitetiedot {
  /** iOS: kaupallinen malli ("iPhone 15 Pro") tai utsname.machine; Android: model ("SM-G991B") */
  malli?: string
  /** iOS: 'Apple'; Android: manufacturer */
  valmistaja?: string
  /** iOS: systemVersion ("17.4"); Android: version.release ja SDK-taso */
  kayttojarjestelmaVersio?: string
  fyysinenLaite?: boolean
}

export interface KayttajanSovellustiedot {
  versio: string
  build: string
}

/**
 * Lemonaid Firestore: customers/{asiakasAvain}/customers-users/{kayttajaAvain}/kayttajan-laitteet/{kayttajaAvain}.
 * KayttajanFirebaseInstallations-dokumentin tokeniton kopio, jonka käyttäjä lukee webistä ja kirjanpitäjä
 * Lemonatorista. Kirjoitetaan samassa batchissa kuin lähde (mobiili-fidTallenna, push-lähetyksen
 * kuolleen tokenin siivous).
 */
export interface KayttajanLaitteet {
  asiakasAvain: string
  kayttajaAvain: string
  laitteet: { [fid: string]: KayttajanLaite }
}

export interface KayttajanLaite extends Omit<KayttajanFid, 'token'> {
  tokenSaatavilla: boolean
  /** Rekisteröinti poistettiin lähdedokumentista, koska FCM ilmoitti tokenin kuolleeksi tai käyttäjä kirjautui laitteelta ulos. Uudelleenrekisteröinti poistaa merkinnän. */
  rekisterointiPoistettu?: Timestamp
}

export type KayttajanTunnistamisStatus = 'ei-tunnistettu' | 'tunnistettu-hetulla' | 'tunnistettu-nets'
export type SopimuksenHyvaksyntaStatus = 'hyvaksytty' | 'ei-hyvaksytty'
export type TuntemistietojenStatus = 'ei-annettu' | 'vaatii-paivityksen' | 'annettu'

export interface Tilikausi {
  avain: string
  alkaa: LocalDate
  loppuu: LocalDate
  veroilmoitusSent: Timestamp
  lukittu: boolean
}

export enum KirjanpidonPeruste {
  LASKU = 'LASKU',
  SUORITE = 'SUORITE',
  MAKSU = 'MAKSU',
}

export interface AsiakkaanKirjanpidonPeruste {
  alkaa: LocalDate | null
  loppuu: LocalDate | null
  peruste: KirjanpidonPeruste
  kirjanpitoReskontraAlkaen?: LocalDate | null
}

export interface Asiakas {
  asiakasId: string
  asiakasAvain: string
  nimi: string
  ytunnus: string
  yritysmuoto: Yritysmuoto
  kasittelija: string
  kasittelijavara: string
  paivitetty: Timestamp
  maksutavat: AsiakkaanMaksutapa[]
  sopimuksenHyvaksyntaStatus: SopimuksenHyvaksyntaStatus
  tuntemistietojenStatus: TuntemistietojenStatus
  katuosoite: string
  postinro: string
  postitmp: string
  maakoodi: string
  ossRekisterissa?: 1
  suomiFiValtuutusPuuttuu?: 1
  /**
   * Milloin työkielto asetettiin. Kenttä on olemassa vain, kun työkielto on voimassa.
   */
  tyokieltoAlku?: Timestamp
  tilikaudet: Tilikausi[]
  /**
   * Millä perusteella kirjanpito tehdään?
   */
  kirjanpidonPerusteet?: AsiakkaanKirjanpidonPeruste[]
  /**
   * Maksutilien tiedot, joilla asiakas voi maksaa maksuja
   */
  paymentConfigs?: LemonaidPaymentConfig[]
  /**
   * Onko asiakkaalla aktiivisia henkilöitä, joilla on
   * maksun hyväksyjä rooli päällä?
   */
  paymentsNeedsApproval?: true
  /**
   * Kuinka monta hyväksyntää tarvitaan, jotta maksu katsotaan hyväksytyksi?
   */
  paymentsNumberOfApprovalsRequired?: number
  sopimuskaudet?: LemonaidAsiakkaanSopimuskausi[]
  /**
   * Tilintarkastajat joilla on oikeus tähän asiakkaaseen.
   * Avain on tilintarkastajan avain / uid, arvo on voimassaolon päättymispäivä.
   */
  tilintarkastajat?: { [tilintarkastajaAvain: string]: Timestamp }

  /**
   * Pakottaa Lemonaid-mobiilin moodin; puuttuessa käyttäjä saa valita itse.
   * Asetetaan toistaiseksi käsin suoraan Firestoreen.
   */
  mobileAppMode?: 'ai' | 'standard'
  /**
   * Sallii diagnostiikkanäkymän Lemonaid-mobiilissa.
   * Asetetaan toistaiseksi käsin suoraan Firestoreen.
   */
  mobileDiagnostics?: boolean

  rekisterit?: {
    /** Arvonlisäverovelvollisuus */
    alv: {
      /** Liiketoiminnasta arvonlisäverovelvollinen */
      tyyppi: YtjAlvrekisterinTila
      alkaen: NumberDate
    }
    /** Ennakkoperintärekisteri */
    ennakko: {
      alkaen: NumberDate
    }
    /** Yhdistysrekisteri */
    yhdistys: {
      alkaen: NumberDate
    }
    /** Kaupparekisteri */
    kauppa: {
      alkaen: NumberDate
    }
    /** Työnantajarekisteri */
    tyonantaja: {
      alkaen: NumberDate
    }
    /** Verohallinnon perustiedot */
    verohallinto: {
      alkaen: NumberDate
    }
  }
}

export interface LemonaidPaymentConfig {
  avain: string
  /**
   * Mistä päivästä lähtien maksutili on aktiivinen?
   */
  start: NumberDate
  /**
   * Milloin maksutilin aktiivisuus päättyy?
   */
  end: NumberDate
  /**
   * Miltä tililtä maksut suoritetaan?
   */
  iban: string
  /**
   * Mikä pankki kyseessä? (IBANista pääteltävä BIC ei kerro pankkia kaikissa tapauksissa esim. säästöpankkien ITELFIHH)
   */
  bank: PaymentSupportedBank
  /**
   * Pankin antama asiakkaan yksilöivä tunniste
   */
  debtorIdentifier: string
  /**
   * Integraation tyyppi
   */
  type: 'avalo'
}

export interface LemonaidAsiakkaanSopimuskausi {
  alkaa: LocalDate
  loppuu: LocalDate
  tauot: LemonaidAsiakkaanSopimuskaudenTauko[]
  sopimusversioAvain: string // Added for debugging purposes
  palvelupaketti: SopimusPalvelupaketti
  lisapalvelut: { [palvelu in SopimusLisapalvelu]?: boolean }
  edellinen?: Omit<LemonaidAsiakkaanSopimuskausi, 'tauot'>
  paivitetty: Timestamp
  versioVoimassaAlkaen: NumberDate
}

export interface LemonaidAsiakkaanSopimuskaudenTauko {
  alkaa: LocalDate
  loppuu: LocalDate
  tyyppi: 'tauko' | 'poytalaatikko'
}

/** @deprecated Use LemonaidAsiakkaanKuukausihinta instead. This interface uses LocalDate and will be removed after data migration is complete. */
export interface LemonaidAsiakkaanHinta {
  hinta: number
  voimassaAlkaen: LocalDate
}

export interface LemonaidAsiakkaanKuukausihinta {
  hinta: number
  voimassaAlkaen: NumberDate
  voimassaLoppuen: NumberDate
}
export interface CustomerPrices {
  asiakasAvain: string
  hinnat: LemonaidAsiakkaanKuukausihinta[]
  muutJatkuvatVeloitukset?: LemonaidAsiakkaanMuuJatkuvaVeloitus[]
}

export interface LemonaidAsiakkaanMuuJatkuvaVeloitus {
  avain: string
  tuote: LaskutettavatPalvelut
  hinta: number
  frekvenssi: LaskutusFrekvenssi
  voimassaAlkaen: LocalDate
  poistettu: boolean
  paattyy?: LocalDate
  lisatiedot?: string
}

export enum SopimusPalvelupaketti {
  TMI_MAKSUPERUSTEINEN = 'TMI_MAKSUPERUSTEINEN',
  TMI_LASKUPERUSTEINEN = 'TMI_LASKUPERUSTEINEN',
  OY_MAKSUPERUSTEINEN = 'OY_MAKSUPERUSTEINEN',
  OY_LASKUPERUSTEINEN = 'OY_LASKUPERUSTEINEN',
  OY_KAPITALISAATIOSOPIMUS = 'OY_KAPITALISAATIOSOPIMUS',
  KASVU_SUORITEPERUSTEINEN = 'KASVU_SUORITEPERUSTEINEN',
  VAIN_PALKAT = 'VAIN_PALKAT',
  HOLVI_ZEN = 'HOLVI_ZEN',
  MUU = 'MUU',
}

export enum SopimusLisapalvelu {
  PALKAT = 'PALKAT',
  ENNAKKOVEROJEN_SEURANTA = 'ENNAKKOVEROJEN_SEURANTA',
  OSTOLASKUJEN_MAKSAMINEN = 'OSTOLASKUJEN_MAKSAMINEN',
  KIRJANPIDON_RESKONTRA = 'KIRJANPIDON_RESKONTRA',
  MYYNTILASKUJEN_LAHETTAMINEN = 'MYYNTILASKUJEN_LAHETTAMINEN',
  RESKONTRAN_HOITO = 'RESKONTRAN_HOITO',
  SAATAVIEN_VALVONTA = 'SAATAVIEN_VALVONTA',
}

export interface TositteenLisaysprosessinOsaMobiilissa {
  tunniste: TositteenLisaysprosessinOsanTunniste
  otsikko: string
}

export interface TositteenLisaysprosessinAlvVahennysoikeusPorras {
  arvo: number
  oletus: boolean
}

export interface CustomerKuukausienStatuksetKirjanpidossa {
  [kuukausi: NumberMonth]: CustomerKuukaudenStatusKirjanpidossa
}
export type CustomerKuukaudenStatusKirjanpidossa = 'ok' | 'not-ok'

export enum TositteenLisaysprosessinOsanTunniste {
  KAMERA = 'cameraview',
  MAKSUTAPA = 'editpaymentmethod',
  MAKSUN_MAARA = 'editamount',
  PVM_SELITE = 'editdatedesc',
  ALV_VAHENNYSOIKEUS = 'editalv',
}

export const TOSITTEEN_LISAYSPROSESSIN_OTSIKOT_MAP: Map<TositteenLisaysprosessinOsanTunniste, string> = new Map<TositteenLisaysprosessinOsanTunniste, string>([
  [TositteenLisaysprosessinOsanTunniste.KAMERA, 'Kamera'],
  [TositteenLisaysprosessinOsanTunniste.MAKSUTAPA, 'Tosite on maksettu'],
  [TositteenLisaysprosessinOsanTunniste.MAKSUN_MAARA, 'Maksun määrä'],
  [TositteenLisaysprosessinOsanTunniste.PVM_SELITE, 'Lisätiedot'],
  [TositteenLisaysprosessinOsanTunniste.ALV_VAHENNYSOIKEUS, 'Alv:n vähennysoikeus'],
])

export interface AsiakkaanMaksutapa {
  aktiivinen: boolean
  tunniste: number
  nimi: string
  base64Kuva: string
  iban: string
  tilitapahtumatSynkronoidaanAlkaen: LocalDate
  tilitapahtumatSynkronoidaanLoppuen: LocalDate
  sahkoisenTiliotteenAlatyyppi?: SahkoisenTiliotteenAlatyyppi
  omallaRahallaUid?: string
}

export interface TositteenMaksutapa {
  aktiivinen: boolean
  oletus: boolean
  tunniste: number
  nimiListauksessa: string
  nimiMuokkauksessa: string
  base64Kuva: string
}

export const MAKSUTAPA_MYYNTITOSITE: TositteenMaksutapa = {
  aktiivinen: true,
  // eslint-disable-next-line max-len
  base64Kuva:
    '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',
  nimiListauksessa: 'Myyntitositteet',
  nimiMuokkauksessa: 'Myyntitositteet',
  oletus: false,
  tunniste: MYYNTITOSITE_MAKSUTAPA_ID,
}

export const MAKSUTAPA_PALKKATOSITE: TositteenMaksutapa = {
  aktiivinen: true,
  // eslint-disable-next-line max-len
  base64Kuva:
    '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',
  nimiListauksessa: 'Palkkamateriaali',
  nimiMuokkauksessa: 'Palkkamateriaali',
  oletus: false,
  tunniste: PALKKATOSITE_MAKSUTAPA_ID,
}

export const MAKSUTAPA_TILIOTETOSITE: TositteenMaksutapa = {
  aktiivinen: true,
  // eslint-disable-next-line max-len
  base64Kuva:
    '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',
  nimiListauksessa: 'Tiliotteet',
  nimiMuokkauksessa: 'Tiliotteet',
  oletus: false,
  tunniste: TILIOTETOSITE_MAKSUTAPA_ID,
}

export const MAKSUTAPA_MYYNTILASKU: TositteenMaksutapa = {
  aktiivinen: true,
  // eslint-disable-next-line max-len
  base64Kuva:
    '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',
  nimiListauksessa: 'Myyntilaskut',
  nimiMuokkauksessa: 'Myyntilaskut',
  oletus: false,
  tunniste: MYYNTILASKU_MAKSUTAPA_ID,
}

export const MAKSUTAPA_MUU: TositteenMaksutapa = {
  aktiivinen: true,
  // eslint-disable-next-line max-len
  base64Kuva:
    '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',
  nimiListauksessa: 'Muut tositteet',
  nimiMuokkauksessa: 'Muut tositteet',
  oletus: false,
  tunniste: MUU_MAKSUTAPA_ID,
}
export const MAKSUTAPA_SAHKOISET_LASKUT: TositteenMaksutapa = {
  aktiivinen: false,
  // eslint-disable-next-line max-len
  base64Kuva:
    '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',
  nimiListauksessa: 'Sähköiset laskut',
  nimiMuokkauksessa: 'Sähköiset laskut',
  oletus: false,
  tunniste: SAHKOISET_LASKUT_MAKSUTAPA_ID,
}

export const MAKSUTAPA_SAHKOISET_LASKUT_FAKE: TositteenMaksutapa = {
  aktiivinen: false,
  // eslint-disable-next-line max-len
  base64Kuva:
    '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',
  nimiListauksessa: 'Ostolaskut',
  nimiMuokkauksessa: 'Ostolaskut',
  oletus: false,
  tunniste: SAHKOISET_LASKUT_FAKE_MAKSUTAPA_ID,
}

export const MAKSUTAPA_AUTOMAATTISET_JAKSOTUKSET: TositteenMaksutapa = {
  aktiivinen: false,
  // eslint-disable-next-line max-len
  base64Kuva:
    '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',
  nimiListauksessa: 'Automaattiset jaksotukset',
  nimiMuokkauksessa: 'Automaattiset jaksotukset',
  oletus: false,
  tunniste: AUTOMAATTISET_JAKSOTUKSET_ID,
}

/** Suorittavan käyttäjän tiedot */
export interface KayttajanTiedot {
  /** Toimintoa suorittavan käyttäjän avain */
  uid: string
  /** Jos käyttäjää emuloidaan, emuloijan avain */
  euid?: string
  /** Toimintoa suorittavan käyttäjän asiakasId */
  asiakasId: string
  /** Toimintoa suorittavan käyttäjän asiakas avain */
  asiakasAvain: string
}
export interface KnowYourCustomer extends BaseEntity {
  asiakasAvain: string
  osakkaat: CustomerOsakas[]
  hallitus: CustomerHallituksenJasen[]
  edunsaajat: CustomerBeneficiary[]
}

export interface KnowYourCustomerSeenLog {
  kayttajaAvain: string
  seenAt: Timestamp
}

export interface BasePerson {
  nimi: string
  hetuTaiYtunnus: string
  kansallisuus: string
  pep: boolean
  noFinnishId?: boolean
  birthDate?: LocalDate
}

export interface CustomerOsakas extends BasePerson {
  osakkeita: number
  onkoYritys?: boolean
}

export interface UserKnowYourCustomer {
  luotu: Timestamp
  /** Is a politically exposed person */
  pep: boolean
  kansallisuus: string
  tunnistajanNimi: string
}

export type CustomerHallituksenJasenRole = 'puheenjohtaja' | 'jasen' | 'toimitusjohtaja' | 'varajasen'
export interface CustomerHallituksenJasen extends BasePerson {
  rooli?: CustomerHallituksenJasenRole
}

export interface CustomerBeneficiary extends BasePerson {}

export interface PaivitaAsiointikieliRequest {
  kieli: TuettuKieli
}

export interface PaivitaAsiointikieliResponse extends ErrorResponse<'unknown-error' | 'missing-data' | 'unauthorized'> {}

export interface Ajopaivakirja extends BaseEntity {
  asiakasAvain: string
  kokonaisKmMaara: number
  tyoAjoissaKm: number
  onkoTyoajoissaYliPuoli: boolean
  tiedotPerustuvat: 'ajopaivakirjaan' | 'muuhun selvitykseen'
  yli10hKotimaanMatkojenLukumaara: number
  yli6hKotimaanMatkojenLukumaara: number
  ulkomaanMatkapaivienLukumaara: number
  ulkomaanPaivarahojenYhteismaara: number
  kotiYrityksenTyotilana: 'ei-vahennysta' | 'paasaantoinen' | 'osa-aikainen' | 'satunnainen'
}
export interface KirjanpidonUlkopuolisetKulut extends BaseEntity {
  asiakasAvain: string
  vuosi: number
  ajoneuvot: Ajoneuvo[]
  tiedotPerustuvat: 'ajopaivakirjaan' | 'muuhun selvitykseen'
  yli10hKotimaanMatkojenLukumaara: number
  yli6hKotimaanMatkojenLukumaara: number
  ulkomaanMatkapaivienLukumaara: number
  ulkomaanPaivarahojenYhteismaara: number
  kotiYrityksenTyotilana: 'ei-vahennysta' | 'paasaantoinen' | 'osa-aikainen' | 'satunnainen'
  eiIlmoitettavaa?: boolean
}

export interface Ajoneuvo {
  rekisteritunnus: string
  tyyppi: 'henkilo' | 'paketti' | 'muu'
  kokonaisKmMaara: number
  tyoAjoissaKm: number
}

export enum NewFeature {
  LASKUJEN_VASTAANOTTO = 'laskujen-vastaanotto',
  MAKSUT_LASKUJEN_VASTAANOTTO = 'maksut-laskujen-vastaanotto',
  MAKSUT_LASKUJEN_VASTAANOTTO_MAINOS = 'maksut-laskujen-vastaanotto-mainos',
  MAKSUT_MAKSAMINEN = 'maksut-maksaminen',
  AUTOMAATTINEN_RESKONTRA = 'automaattinen-reskontra',
  ASIAKASKYSELY = 'asiakaskysely',
  TOISTUVAT_LASKUT = 'toistuvat-laskut',
  TOSITTEET_UUDISTUS = 'tositteet-uudistus',
}

export enum MaksutLaskujenVastaanottoMainosOption {
  EHKA_MYOHEMMIN = 'ehka-myohemmin', // User clicked Ehkä myöhemmin in the pop-up
  OTA_KAYTTOON = 'ota-kayttoon', // User clicked Ota käyttöön in the pop-up and was directed to the sign-up page
  IKKUNA_SULJETTIIN = 'ikkuna-suljettiin', // User closed the pop-up manually by clicking the close btn at the top right corner
}

export type NewFeatureUserActionOption = MaksutLaskujenVastaanottoMainosOption /** Add new option enums for each new feature */

export interface UserSettings {
  asiakasAvain: string
  kayttajaAvain: string
  seen?: { [featureKey: string]: true }
  shownUntil?: { [featureKey: string]: LocalDate }
  userAction?: { [featureKey: string]: NewFeatureUserActionOption }
  /**
   * Valittu maksukoodi
   */
  vm?: 'qr' | 'viiva' | 'virtuaaliviiva'
}

export interface AnnaLemonaidKayttajienTiedotRequest {}

export interface AnnaLemonaidKayttajienTiedotResponse extends ErrorResponse {
  avaimetJaNimet: Pick<Kayttaja, 'avain' | 'etunimi' | 'sukunimi'>[]
}

export type TyokieltoTapahtumaTyyppi = 'asetettu' | 'poistettu-manuaalisesti' | 'poistunut-automaattisesti'

export interface KayttajaYhteystietoMuutosViestiTyojono extends TyojononMerkintaBase {
  asiakasAvain: string
  kayttajaAvain: string
  vanhaEmail?: string | null
  uusiEmail: string
  vanhaPuhelin?: string | null
  uusiPuhelin: string
}

export enum KayttajaYhteystietoMuutostyyppi {
  PUHELIN = 'puhelin',
  EMAIL = 'email',
  PUHELIN_JA_EMAIL = 'puhelin_ja_email',
}
